import { createFileRoute, notFound } from "@tanstack/react-router";
import {
  Server, Globe, Users, Lock, Database, LifeBuoy,
  Shield, Zap, HardDrive, Mail, Headphones, RefreshCw, Bell, Clock, KeyRound, Award,
} from "lucide-react";
import { ServicePage, type ServicePageData } from "@/components/sections/service-page";

const CHILDREN: Record<string, ServicePageData & { metaDesc: string }> = {
  "shared-hosting": {
    eyebrow: "Hosting · Plans",
    title: "Shared Hosting",
    sub: "Affordable, fast shared hosting on NVMe + LiteSpeed.",
    metaDesc: "Shared hosting on NVMe SSD with LiteSpeed, free SSL, daily backups and 24/7 support — from $4/mo.",
    intro:
      "Shared hosting is the most cost-effective way to launch a website. You share a powerful server with other sites, but our CloudLinux isolation keeps every account fast and stable. Perfect for brochure sites, blogs and small online stores.",
    features: [
      { icon: Zap, title: "NVMe + LiteSpeed", description: "20x faster page loads than legacy SATA hosting." },
      { icon: Shield, title: "Account isolation", description: "CloudLinux LVE prevents noisy-neighbor slowdowns." },
      { icon: Lock, title: "Free SSL", description: "Auto-installed and auto-renewing on every domain." },
      { icon: HardDrive, title: "Daily backups", description: "30-day retention with one-click restore." },
      { icon: Mail, title: "Free email", description: "Unlimited mailboxes with anti-spam." },
      { icon: Headphones, title: "24/7 support", description: "Real engineers, 15-minute response." },
    ],
    process: [
      { step: "01", title: "Choose plan", description: "Pick by sites, storage and traffic." },
      { step: "02", title: "Migrate", description: "Free white-glove move from any host." },
      { step: "03", title: "Configure", description: "SSL, email, DNS — done for you." },
      { step: "04", title: "Launch", description: "Zero-downtime cutover." },
    ],
    deliverables: ["cPanel account", "Free migration", "SSL setup", "Email accounts", "Daily backups"],
    techStack: ["LiteSpeed", "CloudLinux", "cPanel", "Imunify360", "Let's Encrypt"],
    pricing: [
      { name: "Starter", price: "$4", period: "month", features: ["1 website", "10 GB NVMe", "Free SSL + email"] },
      { name: "Business", price: "$10", period: "month", highlighted: true, features: ["10 websites", "50 GB NVMe", "Free CDN + WAF"] },
      { name: "Pro", price: "$25", period: "month", features: ["Unlimited sites", "200 GB NVMe", "Priority support"] },
    ],
    faqs: [
      { q: "Is shared hosting safe?", a: "Yes — CloudLinux isolates every account and Imunify360 blocks malware in real time." },
      { q: "Can I upgrade to VPS later?", a: "Anytime, with zero data loss and no downtime." },
    ],
  },
  "wordpress-hosting": {
    eyebrow: "Hosting · Plans",
    title: "WordPress Hosting",
    sub: "Managed WordPress with one-click installs, staging and auto-updates.",
    metaDesc: "Managed WordPress hosting with LiteSpeed Cache, auto updates, daily backups, staging and free SSL.",
    intro:
      "WordPress-optimized hosting tuned for speed and security. One-click installs, automatic core and plugin updates, free staging environments and a server-side LiteSpeed Cache that beats every caching plugin.",
    features: [
      { icon: Globe, title: "1-click WordPress", description: "Install, clone or migrate in under a minute." },
      { icon: Zap, title: "LiteSpeed Cache", description: "Server-level caching for sub-second loads." },
      { icon: RefreshCw, title: "Auto updates", description: "Core, themes and plugins kept current safely." },
      { icon: Shield, title: "Malware shield", description: "WP-aware WAF blocks brute force and exploits." },
      { icon: HardDrive, title: "Staging", description: "One-click staging copies with easy push-to-live." },
      { icon: Headphones, title: "WP experts", description: "Support staffed by WordPress specialists." },
    ],
    process: [
      { step: "01", title: "Install or migrate", description: "Free move from any WP host." },
      { step: "02", title: "Optimize", description: "Cache, images, fonts and DB tuned." },
      { step: "03", title: "Secure", description: "WAF, 2FA and updates configured." },
      { step: "04", title: "Monitor", description: "Uptime, speed and security alerts." },
    ],
    deliverables: ["Managed WP install", "LiteSpeed Cache", "Staging environment", "Automatic updates", "Daily backups"],
    techStack: ["WordPress", "LiteSpeed", "Redis", "PHP 8.3", "Imunify360", "Cloudflare"],
    pricing: [
      { name: "Single", price: "$8", period: "month", features: ["1 WP site", "25 GB NVMe", "Free SSL"] },
      { name: "Plus", price: "$18", period: "month", highlighted: true, features: ["5 WP sites", "75 GB NVMe", "Staging + CDN"] },
      { name: "Agency", price: "$45", period: "month", features: ["Unlimited WP", "200 GB NVMe", "White-label tools"] },
    ],
    faqs: [
      { q: "Do you support WooCommerce?", a: "Yes — Redis object cache and dedicated WooCommerce tuning are included." },
      { q: "Will auto-updates break my site?", a: "Updates run on staging first with visual regression checks before live promotion." },
    ],
  },
  "reseller-hosting": {
    eyebrow: "Hosting · Plans",
    title: "Reseller Hosting",
    sub: "White-label hosting for agencies and freelancers.",
    metaDesc: "Reseller hosting with WHM, white-label cPanel, free SSL, daily backups and 24/7 support to host client sites.",
    intro:
      "Sell hosting under your own brand. Full WHM access, white-label cPanel, custom name servers and per-client resource limits — backed by our 24/7 support that can speak directly to your clients if you prefer.",
    features: [
      { icon: Users, title: "Unlimited cPanels", description: "Create accounts and packages from WHM." },
      { icon: Award, title: "White-label", description: "Custom name servers and branded panels." },
      { icon: Lock, title: "Free SSL per site", description: "Auto-SSL across every client account." },
      { icon: HardDrive, title: "Per-account backups", description: "Daily backups with isolated restores." },
      { icon: Shield, title: "Account isolation", description: "CloudLinux LVE per client account." },
      { icon: Headphones, title: "End-client support", description: "Optional 24/7 support for your clients." },
    ],
    process: [
      { step: "01", title: "Choose tier", description: "Pick by accounts, storage and bandwidth." },
      { step: "02", title: "Brand", description: "Logo, name servers, packages." },
      { step: "03", title: "Migrate clients", description: "Free bulk migration from any provider." },
      { step: "04", title: "Sell", description: "Bill in your currency, on your terms." },
    ],
    deliverables: ["WHM + cPanel", "White-label setup", "Bulk migration", "Per-client backups", "Optional end-client support"],
    techStack: ["WHM", "cPanel", "LiteSpeed", "CloudLinux", "Imunify360", "WHMCS-ready"],
    pricing: [
      { name: "Starter", price: "$25", period: "month", features: ["30 accounts", "60 GB NVMe", "Free SSL"] },
      { name: "Growth", price: "$60", period: "month", highlighted: true, features: ["100 accounts", "200 GB NVMe", "White-label DNS"] },
      { name: "Agency", price: "$150", period: "month", features: ["Unlimited accounts", "500 GB NVMe", "Priority + end-client support"] },
    ],
    faqs: [
      { q: "Can I bill my clients directly?", a: "Yes — we're fully WHMCS-compatible so you can automate billing in your brand." },
      { q: "Do my clients know it's you?", a: "No — white-label name servers and panels keep us invisible." },
    ],
  },
  "free-ssl": {
    eyebrow: "Hosting · Features",
    title: "Free SSL",
    sub: "Auto-installed, auto-renewing SSL on every domain.",
    metaDesc: "Free SSL certificates auto-installed and auto-renewed on every domain — included on all hosting plans.",
    intro:
      "HTTPS is required for SEO, browser trust and modern features. Every site on our hosting gets a free SSL certificate issued, installed and renewed automatically — no configuration needed.",
    features: [
      { icon: Lock, title: "Auto-issue", description: "Let's Encrypt cert provisioned the moment DNS resolves." },
      { icon: RefreshCw, title: "Auto-renew", description: "Renewals happen 30 days before expiry, automatically." },
      { icon: Shield, title: "Wildcard support", description: "Covers *.yourdomain.com for unlimited subdomains." },
      { icon: KeyRound, title: "HSTS + redirects", description: "HTTP → HTTPS forced with HSTS preloading." },
      { icon: Award, title: "Premium upgrade", description: "EV / OV certificates available on request." },
      { icon: Bell, title: "Expiry alerts", description: "Email alerts if any cert fails to renew." },
    ],
    process: [
      { step: "01", title: "Point DNS", description: "Domain resolves to our servers." },
      { step: "02", title: "Auto-issue", description: "Cert provisioned within minutes." },
      { step: "03", title: "Force HTTPS", description: "HSTS and redirects enabled." },
      { step: "04", title: "Monitor", description: "Continuous renewal monitoring." },
    ],
    deliverables: ["SSL on every domain", "Auto-renewal", "HSTS configured", "HTTPS redirects", "Expiry monitoring"],
    techStack: ["Let's Encrypt", "Sectigo (paid)", "AutoSSL", "OCSP stapling"],
    pricing: [
      { name: "Free SSL", price: "$0", features: ["Let's Encrypt DV", "Wildcard included", "Auto-renew"] },
      { name: "Business OV", price: "$60", period: "year", highlighted: true, features: ["Organization validated", "$1M warranty", "Site seal"] },
      { name: "EV Green Bar", price: "$180", period: "year", features: ["Extended validation", "Green bar", "$1.75M warranty"] },
    ],
    faqs: [
      { q: "Do free SSL certs hurt SEO?", a: "No — Google treats DV, OV and EV equally for ranking purposes." },
      { q: "What if renewal fails?", a: "We alert you, retry automatically, and our team intervenes manually within 1 hour." },
    ],
  },
  "daily-backups": {
    eyebrow: "Hosting · Features",
    title: "Daily Backups",
    sub: "Automated off-site backups with one-click restore.",
    metaDesc: "Daily automated off-site backups with 30-day retention and one-click file or database restores.",
    intro:
      "Backups are your insurance against hacks, bad deploys and human error. We snapshot every account daily to off-site storage, keep 30 days of restore points, and let you restore individual files, databases or the entire account in a single click.",
    features: [
      { icon: Database, title: "Daily snapshots", description: "Full account backup every 24 hours." },
      { icon: HardDrive, title: "Off-site storage", description: "Backups live on separate infrastructure and region." },
      { icon: Clock, title: "30-day retention", description: "Restore any of the last 30 days with one click." },
      { icon: RefreshCw, title: "Granular restore", description: "Restore single files, DBs or full account." },
      { icon: Shield, title: "Encrypted", description: "AES-256 at rest, TLS in transit." },
      { icon: Bell, title: "Failure alerts", description: "Email alerts if any backup misses its window." },
    ],
    process: [
      { step: "01", title: "Enable", description: "On by default — no setup required." },
      { step: "02", title: "Snapshot", description: "Account backed up nightly, encrypted, off-site." },
      { step: "03", title: "Verify", description: "Random restore tests run weekly." },
      { step: "04", title: "Restore", description: "Self-serve in cPanel or via support." },
    ],
    deliverables: ["Nightly backups", "30-day retention", "One-click restore", "Off-site storage", "Restore test logs"],
    techStack: ["JetBackup", "S3-compatible off-site", "AES-256", "cPanel"],
    pricing: [
      { name: "Daily", price: "$0", features: ["30-day retention", "One-click restore", "Off-site storage"] },
      { name: "Hourly", price: "$5", period: "month", highlighted: true, features: ["Hourly snapshots", "60-day retention", "Priority restore"] },
      { name: "Enterprise", price: "Custom", features: ["Real-time replication", "Cross-region DR", "RPO < 5 min"] },
    ],
    faqs: [
      { q: "How fast is a restore?", a: "Most full-account restores complete in under 10 minutes; single-file in seconds." },
      { q: "Can I download a backup?", a: "Yes — full account archives are downloadable from cPanel anytime." },
    ],
  },
  "support-247": {
    eyebrow: "Hosting · Features",
    title: "24/7 Support",
    sub: "Real engineers, 15-minute response, every day of the year.",
    metaDesc: "24/7 hosting support from real engineers with 15-minute response — chat, ticket and phone, every day.",
    intro:
      "Hosting support that actually helps. Our team is in-house, technical and available around the clock — no chatbots, no offshore tier-1 deflection. Average first response is under 15 minutes via chat or ticket.",
    features: [
      { icon: Headphones, title: "Live chat 24/7", description: "Real engineers online every minute of the year." },
      { icon: LifeBuoy, title: "Tickets", description: "Detailed tickets with screen recordings if needed." },
      { icon: Clock, title: "15-min response", description: "Average first response under 15 minutes." },
      { icon: Award, title: "Senior engineers", description: "Tier-1 deflection skipped — talk to experts." },
      { icon: RefreshCw, title: "Free migrations", description: "Support handles your move, free of charge." },
      { icon: Bell, title: "Proactive monitoring", description: "We alert you before you notice issues." },
    ],
    process: [
      { step: "01", title: "Reach out", description: "Chat, ticket or phone." },
      { step: "02", title: "Triage", description: "Senior engineer assigned within 15 min." },
      { step: "03", title: "Resolve", description: "Most issues fixed inside one session." },
      { step: "04", title: "Follow-up", description: "Root-cause + prevention noted in your account." },
    ],
    deliverables: ["24/7 chat + ticket", "Phone support (paid tiers)", "Free migrations", "Proactive monitoring", "RCA on incidents"],
    techStack: ["Live chat", "Ticketing", "Loom", "Status page", "PagerDuty"],
    pricing: [
      { name: "Standard", price: "$0", features: ["24/7 chat + ticket", "15-min response", "Free migrations"] },
      { name: "Priority", price: "$25", period: "month", highlighted: true, features: ["Phone support", "5-min response", "Named engineer"] },
      { name: "Enterprise", price: "Custom", features: ["Dedicated CSM", "1-min SLA", "On-call PagerDuty"] },
    ],
    faqs: [
      { q: "Where is support based?", a: "In-house engineers across US, EU and APAC for follow-the-sun coverage." },
      { q: "Will you fix WordPress / app issues?", a: "Yes — we go above the server level for common stacks (WP, Laravel, Node) at no extra cost." },
    ],
  },
};

export const Route = createFileRoute("/hosting/web/$child")({
  loader: ({ params }) => {
    const item = CHILDREN[params.child];
    if (!item) throw notFound();
    return { item, slug: params.child };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const { item } = loaderData;
    const url = `/hosting/web/${params.child}`;
    return {
      meta: [
        { title: `${item.title} — Web Hosting | CoderLity` },
        { name: "description", content: item.metaDesc },
        { property: "og:title", content: `${item.title} — Web Hosting | CoderLity` },
        { property: "og:description", content: item.metaDesc },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: WebHostingChild,
});

function WebHostingChild() {
  const { item } = Route.useLoaderData();
  return <ServicePage data={item} />;
}
